import { useCallback, useRef } from 'react';
import type { RadialBlurValue } from '../../lib/filters/radial-blur';
import type { AdjustmentPanelProps } from '../adjustments/registry';
import { RangeRow } from '../adjustments/RangeRow';

/** Radial Blur panel — mode (Spin/Zoom), amount slider, and a small
 *  2D center picker. The center picker is essential here — text
 *  fields for two normalized coordinates are unusable; the user
 *  needs to SEE where the blur pivots relative to the canvas. */
export function RadialBlurPanel({
  value,
  onChange,
  sourceDimensions,
}: AdjustmentPanelProps<RadialBlurValue>) {
  const set = useCallback(
    (patch: Partial<RadialBlurValue>) => onChange({ ...value, ...patch }),
    [onChange, value],
  );

  return (
    <div className="space-y-3">
      <ModeRow value={value.mode} onChange={(mode) => set({ mode })} />
      <RangeRow
        label="Amount"
        min={0}
        max={100}
        step={1}
        value={value.amount}
        onChange={(v) => set({ amount: v })}
      />
      <CenterPicker
        centerX={value.centerX}
        centerY={value.centerY}
        sourceDimensions={sourceDimensions}
        onChange={(cx, cy) => set({ centerX: cx, centerY: cy })}
      />
    </div>
  );
}

function ModeRow({
  value,
  onChange,
}: {
  value: RadialBlurValue['mode'];
  onChange: (next: RadialBlurValue['mode']) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span id="radial-mode-label" className="text-xs w-16 text-muted-foreground">
        Mode
      </span>
      <div role="radiogroup" aria-labelledby="radial-mode-label" className="flex gap-1 flex-1">
        {(['spin', 'zoom'] as const).map((m) => {
          const active = value === m;
          return (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(m)}
              className={`flex-1 px-2 py-1 text-xs rounded-md border capitalize transition-colors ${
                active
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border text-muted-foreground hover:bg-accent'
              }`}
            >
              {m}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Picker sized to roughly 80px on its long edge, scaled to the
 *  layer's aspect ratio so a portrait source feels portrait and a
 *  wide banner feels wide. Falls back to square when the modal is
 *  in edit mode (no source captured) so the picker still functions.
 *  User clicks or drags to set the center as normalized 0..1
 *  fractions; crosshair shows the current center. */
function CenterPicker({
  centerX,
  centerY,
  sourceDimensions,
  onChange,
}: {
  centerX: number;
  centerY: number;
  sourceDimensions?: { width: number; height: number };
  onChange: (cx: number, cy: number) => void;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  // Long edge ≈ 80px; short edge scales down so the box keeps the
  // layer's aspect ratio. Floors at 24px so an extreme aspect
  // (e.g. 10000×100) doesn't collapse the short axis to invisibility.
  const LONG_EDGE = 80;
  const MIN_SHORT_EDGE = 24;
  let boxW = LONG_EDGE;
  let boxH = LONG_EDGE;
  if (sourceDimensions && sourceDimensions.width > 0 && sourceDimensions.height > 0) {
    const { width, height } = sourceDimensions;
    if (width >= height) {
      boxW = LONG_EDGE;
      boxH = Math.max(MIN_SHORT_EDGE, Math.round((height / width) * LONG_EDGE));
    } else {
      boxH = LONG_EDGE;
      boxW = Math.max(MIN_SHORT_EDGE, Math.round((width / height) * LONG_EDGE));
    }
  }

  const pickFromPointer = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const el = boxRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
      onChange(x, y);
    },
    [onChange],
  );

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs w-16 text-muted-foreground">Center</span>
      <div
        ref={boxRef}
        className="relative rounded-md border border-border bg-muted cursor-crosshair touch-none select-none"
        style={{ width: boxW, height: boxH }}
        onPointerDown={(e) => {
          draggingRef.current = true;
          e.currentTarget.setPointerCapture(e.pointerId);
          pickFromPointer(e);
        }}
        onPointerMove={(e) => {
          if (draggingRef.current) pickFromPointer(e);
        }}
        onPointerUp={(e) => {
          draggingRef.current = false;
          e.currentTarget.releasePointerCapture(e.pointerId);
        }}
        onPointerCancel={(e) => {
          draggingRef.current = false;
          if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
          }
        }}
        role="application"
        aria-label="Radial blur center"
      >
        {/* Crosshair guides at current center. */}
        <div
          className="absolute inset-y-0 w-px bg-foreground/30 pointer-events-none"
          style={{ left: `${centerX * 100}%` }}
        />
        <div
          className="absolute inset-x-0 h-px bg-foreground/30 pointer-events-none"
          style={{ top: `${centerY * 100}%` }}
        />
        <div
          className="absolute w-2 h-2 -ml-1 -mt-1 rounded-full bg-primary border border-background pointer-events-none"
          style={{ left: `${centerX * 100}%`, top: `${centerY * 100}%` }}
        />
      </div>
      <div className="text-[10px] text-muted-foreground tabular-nums flex flex-col">
        <span>x {centerX.toFixed(2)}</span>
        <span>y {centerY.toFixed(2)}</span>
      </div>
    </div>
  );
}
